import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';
import { FolderGit2, History, Share2, LayoutDashboard, Clock, MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

const features = [
    {
        title: "Centralized Project Hub",
        description: "Store and organize all your projects in one place. Access your work from anywhere with cloud storage and seamless synchronization.",
        icon: LayoutDashboard,
    },
    {
        title: "Project Organization",
        description: "Keep your projects organized with drafts and published versions. Track changes, manage project states, and maintain a clear overview of your work.",
        icon: FolderGit2,
    },
    {
        title: "Project Lifecycle",
        description: "Manage your projects from draft to deployment. Track status, manage deployments, and keep your team aligned with project progress.",
        icon: History,
    },
    {
        title: "Team Collaboration",
        description: "Share projects with your team, manage permissions, and collaborate in real-time. Built-in tools for team coordination and communication.",
        icon: Share2,
    }
];

const mockProjects = [
    {
        id: "proj-1",
        name: "Sales Dashboard",
        isPublished: true,
        time: new Date().toISOString(),
        preview: "bg-gradient-to-br from-blue-500 to-purple-500"
    },
    {
        id: "proj-2",
        name: "Customer Analytics",
        isPublished: false,
        time: new Date().toISOString(),
        preview: "bg-gradient-to-br from-green-500 to-teal-500"
    },
    {
        id: "proj-3",
        name: "Inventory Tracker",
        isPublished: true,
        time: new Date().toISOString(),
        preview: "bg-gradient-to-br from-orange-500 to-red-500"
    },
    {
        id: "proj-4",
        name: "Marketing Campaign",
        isPublished: false,
        time: new Date().toISOString(),
        preview: "bg-gradient-to-br from-purple-500 to-pink-500"
    }
];

const ProjectsDashboardPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            
            <main className="container mx-auto px-4 py-6 sm:py-12 flex-grow mt-16">
                <div className="mx-auto max-w-[980px]">
                    {/* Hero Section */}
                    <div className="flex flex-col items-center text-center space-y-4 mb-12">
                        <div className="rounded-full bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary">
                            Project Management
                        </div>
                        <h1 className={cn(
                            "text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Your Project Command Center
                        </h1>
                        <p className="mx-auto max-w-[700px] text-lg text-muted-foreground sm:text-xl">
                            Organize, track, and collaborate on all your projects in one place.
                            From drafts to deployments, keep everything organized and accessible.
                        </p>
                    </div>

                    {/* Mock Projects Manager */}
                    <div className="mb-16">
                        <div className="overflow-hidden rounded-lg border bg-background shadow-xl min-h-[600px]">
                            {/* Projects Menu */}
                            <div className="flex justify-between items-center p-4 border-b">
                                <div className="flex gap-2">
                                    <button className="px-3 py-1.5 text-sm rounded-md border bg-gray-100 border-gray-300 font-medium">
                                        All
                                    </button>
                                    <button className="px-3 py-1.5 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50">
                                        Published
                                    </button>
                                    <button className="px-3 py-1.5 text-sm rounded-md border bg-transparent border-gray-200 hover:bg-gray-50">
                                        Drafts
                                    </button>
                                </div>
                                <button className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md font-medium hover:bg-gray-700 transition-colors">
                                    New Project
                                </button>
                            </div>

                            {/* Projects Grid */}
                            <div className="p-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                                    {mockProjects.map((project) => (
                                        <div
                                            key={project.id}
                                            className="border border-gray-200 rounded-lg bg-background w-full hover:shadow-md transition-shadow cursor-pointer relative transition transform hover:scale-[1.02] duration-200"
                                        >
                                            {/* Preview Image */}
                                            <div className="relative">
                                                <div className={cn(
                                                    "w-full h-40 flex items-center justify-center overflow-hidden rounded-t-lg",
                                                    project.preview
                                                )} />
                                                {project.isPublished && (
                                                    <a
                                                        href="#"
                                                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors bg-white/80 rounded-md p-1"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                                            <polyline points="15 3 21 3 21 9"></polyline>
                                                            <line x1="10" y1="14" x2="21" y2="3"></line>
                                                        </svg>
                                                    </a>
                                                )}
                                            </div>

                                            {/* Project Info */}
                                            <div className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex flex-col">
                                                        <h3 className="text-sm font-medium text-foreground truncate max-w-[150px]">
                                                            {project.name}
                                                        </h3>
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <span className={cn(
                                                                "px-2 py-0.5 text-xs font-medium rounded-full",
                                                                project.isPublished
                                                                    ? "bg-green-100 text-green-700"
                                                                    : "bg-gray-100 text-gray-600"
                                                            )}>
                                                                {project.isPublished ? "Published" : "Draft"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(project.time).toLocaleString('en-US', { month: 'short', day: 'numeric' })}
                                                        </div>
                                                        <button className="text-gray-400 hover:text-gray-600 transition-colors">
                                                            <MoreVertical size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats Section */}
                    <div className="mb-16">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <FolderGit2 className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">Active Projects</h3>
                                    <p className="text-2xl font-bold text-primary">24</p>
                                    <p className="text-sm text-muted-foreground">Across 3 teams</p>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.1 }}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <History className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">Published Projects</h3>
                                    <p className="text-2xl font-bold text-primary">12</p>
                                    <p className="text-sm text-muted-foreground">Live and running</p>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.2 }}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <Clock className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">Recent Updates</h3>
                                    <p className="text-2xl font-bold text-primary">156</p>
                                    <p className="text-sm text-muted-foreground">Last 7 days</p>
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 mb-16">
                        {features.map((feature, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="relative overflow-hidden rounded-lg border bg-background p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                                    <feature.icon className="h-6 w-6 text-primary" />
                                </div>
                                <div className="mt-4 space-y-2">
                                    <h3 className="font-bold">{feature.title}</h3>
                                    <p className="text-muted-foreground text-sm">
                                        {feature.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Benefits Section */}
                    <div className="text-center mb-12 sm:mb-16">
                        <h2 className={cn(
                            "text-2xl font-bold tracking-tight sm:text-3xl md:text-4xl mb-4",
                            "bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent"
                        )}>
                            Why Centralize Your Projects?
                        </h2>
                        <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3 mt-8">
                            <div className="p-4">
                                <div className="mb-3 text-2xl">📁</div>
                                <h3 className="font-semibold mb-2">Organized Storage</h3>
                                <p className="text-sm text-muted-foreground">
                                    Keep all your projects organized and easily accessible.
                                </p>
                            </div>
                            <div className="p-4">
                                <div className="mb-3 text-2xl">🔄</div>
                                <h3 className="font-semibold mb-2">Project States</h3>
                                <p className="text-sm text-muted-foreground">
                                    Manage drafts and published versions of your projects.
                                </p>
                            </div>
                            <div className="p-4">
                                <div className="mb-3 text-2xl">👥</div>
                                <h3 className="font-semibold mb-2">Team Access</h3>
                                <p className="text-sm text-muted-foreground">
                                    Share and collaborate with your team seamlessly.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ProjectsDashboardPage; 